import styled from '@emotion/styled';
import { withConfig } from 'src/withConfig';
import { Link } from 'src/domains/layouts/webview/components/router/NewLink';
import { InfoIcon } from 'src/domains/layouts/shared/assets/icons/InfoIcon';

export const RollingDepositLimitForm = styled('form', { label: 'RollingDepositLimitForm' })`
    margin: 0;
`;

export const TextSection = withConfig(theme => styled('p', { label: 'TextSection' })`
    font-size: ${theme.star.fontSize.small};
    line-height: 1.33;
    margin: 8px 0 0 0;
    color: ${theme.star.signUp.txtColor};
`);

export const NoteSection = withConfig(theme => styled(TextSection, { label: 'NoteSection' })`
    color: ${theme.star.signUp.txtColor};
    opacity: 0.85;
    margin-bottom: 16px;
`);

interface SectionHeaderType {
    isUppercase: boolean;
}

export const SectionHeader = withConfig(theme => styled('h4', { label: 'SectionHeader' }) <SectionHeaderType>`
    font-size: ${theme.star.fontSize.regular};
    font-weight: ${theme.star.fontWeight.bold};
    margin: 26px 0 0 0;
    color: ${theme.star.signUp.txtColor};
    text-transform: ${({ isUppercase }): string => isUppercase ? 'uppercase' : 'none'};
`);

export const SectionHeadline = styled('h4', { label: 'SectionHeadline' })`
    display: inline-block;
    margin: 0 5px 0 0;
`;

export const FaqLink = withConfig(theme => styled(Link, { label: 'FaqLink' })`
    display: inline;
    color: ${theme.star.signUp.txtColor};
    font-weight: bold;
    text-decoration: underline;
`);

export const InfoIconWrapper = withConfig(theme => styled(InfoIcon, { label: 'InfoIconWrapper' })`
    position: relative;
    width: 16px;
    top: 3px;
    fill: ${theme.star.signUp.bgColorQuaternary};
    cursor: pointer;
`);

export const SignupDepositLimitsPanelRoot = withConfig(theme => styled('div', { label: 'SignupDepositLimitsPanelRoot' })`
    padding-top: 0;
    width: 100%;
    border-radius: ${theme.star.radius.medium};
    color: ${theme.star.signUp.txtColor};
`);

export const SignupDepositLimitsMainTitle = withConfig(theme => styled('h4', { label: 'SignupDepositLimitsMainTitle' })`
    font-size: ${theme.star.fontSize.xRegular};
    font-weight: ${theme.star.fontWeight.bold};
    line-height: 1.3;
    margin: 12px 0 6px 0;
    color: ${theme.star.signUp.txtColor};
`);

export const SignupDepositLimitsSubtitle = withConfig(theme => styled('p', { label: 'SignupDepositLimitsSubtitle' })`
    margin: 0 0 6px;
    font-size: ${theme.star.fontSize.small};
    line-height: 1.3;
    color: ${theme.star.signUp.txtColor};
`);

/** Same visual language as `SignupDepositLimitAmountShell`: white bar, subtle border, radio on the right. */
export const SignupCalculationTypeRadioGroup = styled('div', { label: 'SignupCalculationTypeRadioGroup' })`
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
    width: 100%;
`;

export const SignupCalculationTypeRadioCard = withConfig(theme => styled('button', { label: 'SignupCalculationTypeRadioCard' })`
    align-items: center;
    appearance: none;
    background-color: ${theme.star.form.dark.bgColor};
    border: 1px solid ${theme.star.form.light.borderColor};
    border-radius: ${theme.star.radius.small};
    color: ${theme.star.form.txtColor};
    cursor: pointer;
    display: flex;
    flex: 1;
    font: inherit;
    gap: ${theme.star.padding.medium};
    justify-content: space-between;
    min-height: 44px;
    min-width: 0;
    padding: 0 ${theme.star.padding.medium};
    position: relative;
    text-align: left;
    &:focus,
    &:focus-visible {
        outline: none;
    }
`);

export const SignupCalculationTypeRadioCaption = withConfig(theme => styled('span', { label: 'SignupCalculationTypeRadioCaption' })`
    font-size: ${theme.star.fontSize.small};
    font-weight: ${theme.star.fontWeight.regular};
    line-height: 1.3;
    color: ${theme.star.form.txtColor};
`);

export const SignupCalculationTypeRadioIndicator = withConfig(theme => styled('span', { label: 'SignupCalculationTypeRadioIndicator' })<{ isSelected: boolean }>`
    align-items: center;
    border-radius: 50%;
    display: flex;
    flex-shrink: 0;
    height: 22px;
    justify-content: center;
    width: 22px;
    border: 2px solid ${theme.star.depositLimits.borderColor};
    &::after {
        background-color: ${({ isSelected }): string =>
            isSelected === true ? theme.star.statuses.success.bgColor : ''};
        border-radius: 50%;
        content: '';
        height: 12px;
        width: 12px;
    }
`);

export const SignupReadonlyCardPeriod = withConfig(theme => styled('span', { label: 'SignupReadonlyCardPeriod' })`
    font-weight: ${theme.star.fontWeight.bold};
    color: ${theme.star.signUp.txtColor};
`);

/** Signup-only amount row: white bar, bold currency left, value/placeholder right (see deposit limits mock). */
export const SignupDepositLimitAmountShell = withConfig(theme => styled('div', { label: 'SignupDepositLimitAmountShell' })<{ hasError: boolean }>`
    align-items: center;
    background-color: ${theme.star.form.dark.bgColor};
    border-radius: ${theme.star.radius.small};
    display: flex;
    margin-top: ${theme.star.padding.small};
    min-height: 44px;
    padding: 0 ${theme.star.padding.medium};
    width: 100%;
    border: 1px solid
        ${({ hasError }): string =>
            hasError === true ? theme.star.statuses.error.bgColor : theme.star.form.light.borderColor};
`);

export const SignupDepositLimitCurrencyPrefix = withConfig(theme => styled('span', { label: 'SignupDepositLimitCurrencyPrefix' })`
    flex-shrink: 0;
    font-size: ${theme.star.fontSize.regular};
    font-weight: ${theme.star.fontWeight.bold};
    color: ${theme.star.form.txtColor};
    margin-right: ${theme.star.padding.small};
`);

export const SignupDepositLimitNativeInput = withConfig(theme => styled('input', { label: 'SignupDepositLimitNativeInput' })`
    background: transparent;
    border: none;
    color: ${theme.star.form.txtColor};
    flex: 1;
    font-size: ${theme.star.fontSize.regular};
    font-weight: ${theme.star.fontWeight.regular};
    min-width: 0;
    outline: none;
    text-align: right;
    height: 40px;

    &::placeholder {
        color: ${theme.star.form.txtColorSecondary};
        opacity: 1;
    }
`);

export const SignupDepositLimitAmountLabelHidden = styled('label', { label: 'SignupDepositLimitAmountLabelHidden' })`
    border: 0;
    clip: rect(0, 0, 0, 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    white-space: nowrap;
    width: 1px;
`;

export const SignupDepositLimitsLoadingText = withConfig(theme => styled('span', { label: 'SignupDepositLimitsLoadingText' })`
    color: ${theme.star.signUp.txtColor};
`);

/** Reserved vertical space while rolling limits fetch; same spinner as `LoadingSpinner` in signup steps. */
export const SignupDepositLimitsLoadingArea = styled('div', { label: 'SignupDepositLimitsLoadingArea' })`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 12px;
    justify-content: center;
    min-height: 280px;
    width: 100%;
`;

export const SignupDepositLimitsEditableCardFrame = withConfig(theme => styled('div', { label: 'SignupDepositLimitsEditableCardFrame' })`
    box-sizing: border-box;
    border-radius: ${theme.star.radius.medium};
`);

/** Muted “Default limit” line inside signup deposit-limit panels. */
export const SignupDepositLimitsDefaultLine = withConfig(theme => styled('div', { label: 'SignupDepositLimitsDefaultLine' })`
    font-size: ${theme.star.fontSize.small};
    line-height: 1.35;
    margin: 4px 0 10px;
    color: ${theme.star.signUp.txtColor};
    opacity: 0.85;
`);
